import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ReactDataTable from 'react-data-table-component';
import { IconPlus, IconEye, IconEdit, IconLoader2 } from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import ActionDropdown from '../../components/common/ActionDropdown';
import { TableLoader } from '../../components/common/Loader';
import { useVehicleRequests, useCreateVehicleRequest, useVehicleRequestSummary } from '../../hooks/useVehicleRequests';
import PassengerEditor, { passengersToPayload } from './components/PassengerEditor';
import LocationEditor, { locationsToPayload, emptyLocationRow } from './components/LocationEditor';
import {
    statusLabel, statusBadgeClass, classifyWorkflowError, formatDate, formatTime,
    dataTableStyles, isForbidden, withoutEmpty, VEH_ACT_PERMISSIONS,
} from './utils';

function StatusBadge({ request }) {
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium whitespace-nowrap ${statusBadgeClass(request.status)}`}>
            {statusLabel(request)}
        </span>
    );
}

function SummaryTiles({ summary }) {
    if (!summary) return null;
    const tiles = [
        { key: 'awaiting_my_action', label: 'Awaiting My Action', accent: 'text-amber-500' },
        { key: 'draft', label: 'Draft' },
        { key: 'pending_review', label: 'Pending Review' },
        { key: 'pending_approval', label: 'Pending Approval' },
        { key: 'approved_unassigned', label: 'Approved, Unassigned' },
        { key: 'assigned', label: 'Assigned' },
        { key: 'in_progress', label: 'In Progress' },
    ];
    return (
        <div className="grid xl:grid-cols-7 md:grid-cols-4 grid-cols-2 gap-15">
            {tiles.map(tile => (
                <div key={tile.key} className="bg-bg-dark rounded-lg shadow-light p-15">
                    <p className={`text-[22px]/[28px] font-semibold ${tile.accent || ''}`}>{summary[tile.key] ?? 0}</p>
                    <p className="text-xs text-text-secondary mt-3">{tile.label}</p>
                </div>
            ))}
        </div>
    );
}

function CreateVehicleRequestModal({ isOpen, onClose, orgId }) {
    const navigate = useNavigate();
    const createRequest = useCreateVehicleRequest();

    const [form, setForm] = useState({
        purpose_of_trip: '',
        departure_date: '',
        departure_time: '',
        return_date: '',
        expected_return_time: '',
    });
    const [passengers, setPassengers] = useState([]);
    const [locations, setLocations] = useState([emptyLocationRow()]);
    const [fieldErrors, setFieldErrors] = useState({});

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const resetForm = () => {
        setForm({ purpose_of_trip: '', departure_date: '', departure_time: '', return_date: '', expected_return_time: '' });
        setPassengers([]);
        setLocations([emptyLocationRow()]);
        setFieldErrors({});
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFieldErrors({});

        if (!form.purpose_of_trip.trim()) {
            setFieldErrors({ purpose_of_trip: 'Purpose of trip is required.' });
            return;
        }
        const locationPayload = locationsToPayload(locations);
        if (locationPayload.length === 0) {
            setFieldErrors({ locations: 'Add at least one location with a state and address.' });
            return;
        }

        // Omitting passengers lets the backend add the requester automatically.
        const passengerPayload = passengersToPayload(passengers);
        const data = {
            ...withoutEmpty({
                purpose_of_trip: form.purpose_of_trip.trim(),
                departure_date: form.departure_date,
                departure_time: form.departure_time,
                return_date: form.return_date,
                expected_return_time: form.expected_return_time,
            }),
            locations: locationPayload,
            ...(passengerPayload.length > 0 && { passengers: passengerPayload }),
        };

        createRequest.mutate({ data, orgId }, {
            onSuccess: (res) => {
                toast.success(`Vehicle request ${res.reference_no || ''} created.`.trim());
                handleClose();
                navigate(`/vehicle-requests/${res.id}`);
            },
            onError: (err) => {
                const { fieldErrors: errors, message } = classifyWorkflowError(err);
                setFieldErrors(errors);
                toast.error(message);
            },
        });
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-15"
            onClick={handleClose}
        >
            <div
                className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-20 border-b border-border">
                    <h5 className="text-[16px]/[22px] font-semibold text-center">New Vehicle Request</h5>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="p-20 flex flex-col gap-15">
                        <div className="form-group">
                            <label className="block text-sm font-medium mb-5">
                                Purpose of Trip <span className="text-danger">*</span>
                            </label>
                            <textarea
                                value={form.purpose_of_trip}
                                onChange={(e) => setField('purpose_of_trip', e.target.value)}
                                placeholder="e.g. Supportive supervision visit to 4 facilities"
                                rows={2}
                                className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none ${fieldErrors.purpose_of_trip ? 'border-danger' : 'border-border'}`}
                            />
                            {fieldErrors.purpose_of_trip && (
                                <p className="text-danger text-xs mt-5">{fieldErrors.purpose_of_trip}</p>
                            )}
                        </div>

                        <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                            <div className="form-group">
                                <label className="block text-sm font-medium mb-5">Departure Date</label>
                                <input
                                    type="date"
                                    value={form.departure_date}
                                    onChange={(e) => setField('departure_date', e.target.value)}
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                                />
                            </div>
                            <div className="form-group">
                                <label className="block text-sm font-medium mb-5">Departure Time</label>
                                <input
                                    type="time"
                                    value={form.departure_time}
                                    onChange={(e) => setField('departure_time', e.target.value)}
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                                />
                            </div>
                            <div className="form-group">
                                <label className="block text-sm font-medium mb-5">Return Date</label>
                                <input
                                    type="date"
                                    value={form.return_date}
                                    onChange={(e) => setField('return_date', e.target.value)}
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                                />
                            </div>
                            <div className="form-group">
                                <label className="block text-sm font-medium mb-5">Expected Return Time</label>
                                <input
                                    type="time"
                                    value={form.expected_return_time}
                                    onChange={(e) => setField('expected_return_time', e.target.value)}
                                    className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                                />
                            </div>
                        </div>

                        <LocationEditor
                            value={locations}
                            onChange={setLocations}
                            error={fieldErrors.locations}
                        />

                        <PassengerEditor
                            value={passengers}
                            onChange={setPassengers}
                            orgId={orgId}
                            error={fieldErrors.passengers}
                        />
                    </div>

                    <div className="flex gap-10 p-20 border-t border-border">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="btn btn-outline-secondary flex-1"
                            disabled={createRequest.isPending}
                        >
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary flex-1" disabled={createRequest.isPending}>
                            {createRequest.isPending ? (
                                <span className="flex items-center justify-center gap-5">
                                    <IconLoader2 size={14} className="animate-spin" /> Saving...
                                </span>
                            ) : 'Save Draft'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function VehicleRequests() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    // Viewing the list doesn't imply being allowed to create a request.
    const canAct = hasAnyPermission(VEH_ACT_PERMISSIONS);

    const [createModal, setCreateModal] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(filterText), 400);
        return () => clearTimeout(timer);
    }, [filterText]);

    // Server-side filtering — search spans fields the trimmed list doesn't carry.
    const filters = useMemo(() => withoutEmpty({
        status: statusFilter,
        search: debouncedSearch,
        from: dateRange.from,
        to: dateRange.to,
    }), [statusFilter, debouncedSearch, dateRange]);

    const { data: requests = [], isLoading, error } = useVehicleRequests(orgId, filters);
    const { data: summary } = useVehicleRequestSummary(orgId);

    const columns = [
        {
            name: 'Reference',
            selector: row => row.reference_no,
            sortable: true,
            cell: row => (
                <div>
                    <p className="font-medium text-sm">{row.reference_no || '-'}</p>
                    <p className="text-xs text-text-secondary mt-1">{row.requester_name || ''}</p>
                </div>
            ),
            minWidth: '160px',
        },
        {
            name: 'Purpose',
            selector: row => row.purpose_of_trip,
            cell: row => <span className="text-sm">{row.purpose_of_trip || '-'}</span>,
            minWidth: '220px',
        },
        {
            name: 'Departure',
            selector: row => row.departure_date,
            sortable: true,
            cell: row => (
                <div className="text-sm">
                    <p>{formatDate(row.departure_date)}</p>
                    {row.departure_time && (
                        <p className="text-xs text-text-secondary mt-1">{formatTime(row.departure_time)}</p>
                    )}
                </div>
            ),
            width: '140px',
        },
        {
            name: 'Vehicle',
            selector: row => row.vehicle_reg_no,
            cell: row => (
                <div className="text-sm">
                    <p>{row.vehicle_reg_no || '-'}</p>
                    {row.driver_name && <p className="text-xs text-text-secondary mt-1">{row.driver_name}</p>}
                </div>
            ),
            width: '150px',
        },
        {
            name: 'Status',
            selector: row => row.status,
            sortable: true,
            cell: row => <StatusBadge request={row} />,
            width: '170px',
        },
        {
            name: 'Actions',
            cell: row => (
                <ActionDropdown
                    menuItems={[
                        {
                            label: 'View Details',
                            icon: IconEye,
                            onClick: () => navigate(`/vehicle-requests/${row.id}`),
                            description: 'View the full request',
                        },
                        ...(['draft', 'rejected'].includes(row.status) ? [{
                            label: 'Edit Request',
                            icon: IconEdit,
                            onClick: () => navigate(`/vehicle-requests/${row.id}`),
                            description: 'Edit and resubmit',
                        }] : []),
                    ]}
                />
            ),
            width: '80px',
            center: true,
        },
    ];

    const subHeaderComponent = useMemo(() => (
        <div className="flex gap-5 mb-10 w-full justify-end flex-wrap items-center">
            <span className="text-xs text-text-secondary">Departing</span>
            <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            />
            <span className="text-xs text-text-secondary">to</span>
            <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            />
            <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-bg-light border border-border py-[6px] px-3 min-h-[40px] rounded-[4px] focus:outline-none"
            >
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="pending_review,pending_approval">Awaiting decision</option>
                <option value="approved">Approved</option>
                <option value="assigned,in_progress">On the road</option>
                <option value="completed">Completed</option>
                <option value="rejected,cancelled">Rejected / Cancelled</option>
            </select>
            <input
                type="text"
                placeholder="Search requests..."
                className="bg-bg-light border border-border w-full max-w-[250px] py-[6px] px-3 min-h-[40px] rounded-[4px] placeholder:text-text-secondary focus:outline-none"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
            />
        </div>
    ), [filterText, statusFilter, dateRange]);

    if (isForbidden(error)) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Vehicle Requests" path="Vehicle / My Requests" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    You do not have access to vehicle requests. Contact your administrator if you need to raise one.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Vehicle Requests" path="Vehicle / My Requests" />

            <SummaryTiles summary={summary} />

            <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                <div className="flex justify-between items-center mb-20 flex-wrap gap-10">
                    <h2 className="text-lg font-bold">My Vehicle Requests</h2>
                    {canAct && (
                        <button
                            onClick={() => setCreateModal(true)}
                            className="btn btn-primary flex items-center gap-5"
                        >
                            <IconPlus size={16} />
                            New Request
                        </button>
                    )}
                </div>

                {isLoading ? (
                    <TableLoader />
                ) : (
                    <ReactDataTable
                        columns={columns}
                        data={requests}
                        pagination
                        subHeader
                        subHeaderComponent={subHeaderComponent}
                        highlightOnHover
                        pointerOnHover
                        persistTableHead
                        onRowClicked={(row) => navigate(`/vehicle-requests/${row.id}`)}
                        noDataComponent={
                            <div className="py-40 text-center text-text-secondary text-sm">
                                No vehicle requests found. Click "New Request" to get started.
                            </div>
                        }
                        customStyles={dataTableStyles}
                    />
                )}
            </div>

            <CreateVehicleRequestModal
                isOpen={createModal}
                onClose={() => setCreateModal(false)}
                orgId={orgId}
            />
        </div>
    );
}
